import { twoDigitNum, checkLang, formatTimeMin, hardExpired } from '../helpers';
import {
  localStorageLang,
  EXTRA_FEED_SEPARATOR,
  FEED_URLSRC_CMS_V2,
  OE_LIVE_WINDOW_HOURS,
  OE_CATCHUP_WINDOW_HOURS,
} from '../constants';
import { globalFetch } from './globalActions';
import {
  EXTRAFEED_HAS_ERRORED,
  EXTRAFEED_IS_LOADING,
  EXTRAFEED_FETCH_DATA_SUCCESS,
  EXTRAFEED_HOME_HAS_ERRORED,
  EXTRAFEED_HOME_IS_LOADING,
  EXTRAFEED_HOME_FETCH_DATA_SUCCESS,
  EXTRAFEED_DAY_HAS_ERRORED,
  EXTRAFEED_DAY_IS_LOADING,
  EXTRAFEED_DAY_FETCH_DATA_SUCCESS,
} from './types';
import { uiStrings as strings } from './ui_strings';

/////////////////
// Extra Feeds //
/////////////////

function adjustEndDateTime(startLiveDateTime, endDateTime, windowHours) {
  let adjustedDate;

  const startDateTime = new Date(startLiveDateTime);
  const endDateTimeObj = new Date(endDateTime);

  if (endDateTimeObj - startDateTime > windowHours * 60 * 60 * 1000) {
    adjustedDate = new Date(
      startDateTime.getTime() + windowHours * 60 * 60 * 1000
    );
  } else {
    adjustedDate = new Date(endDateTimeObj);
  }
  return adjustedDate.toISOString();
}

function catchUpTodaysDateFormat(date) {
  let dateString;
  if (!date || date === '') {
    const currentDate = new Date();
    dateString = `${currentDate.getFullYear()}-${twoDigitNum(
      currentDate.getMonth() + 1
    )}-${twoDigitNum(currentDate.getDate())}`;
  } else {
    dateString = date;
  }

  return dateString;
}

export function fetchExtraFeedHome() {
  return globalFetch({
    urlPath: 'online-exclusive-cards',
    extraParams: '&page=1&pageSize=20&days=0',
    urlSrc: FEED_URLSRC_CMS_V2,
    reducerIsLoading: EXTRAFEED_HOME_IS_LOADING,
    reducerError: EXTRAFEED_HOME_HAS_ERRORED,
    dispatchSuccess: (itemObj, dispatch) => {
      dispatch(fetchExtraFeedHomeSuccess(itemObj));
    },
  });
}

export function fetchExtraFeedHomeSuccess(items) {
  const lang =
    localStorage.getItem(localStorageLang) === null
      ? 0
      : parseInt(localStorage.getItem(localStorageLang), 10);

  let streams = [];
  const rightNow = new Date();

  items.results &&
    items.results.length > 0 &&
    items.results.forEach((value, key) => {
      const startLiveDateTime = value.startLiveDateTime;
      const endDateTime = adjustEndDateTime(
        startLiveDateTime,
        value.endDateTime,
        OE_LIVE_WINDOW_HOURS
      );
      const streamExpireDateTime = value.timeShifted
        ? adjustEndDateTime(
            startLiveDateTime,
            value.streamExpireDateTime,
            OE_CATCHUP_WINDOW_HOURS
          )
        : null;

      if (!hardExpired(value.timeShifted, endDateTime, streamExpireDateTime)) {
        const isLive =
          rightNow > new Date(startLiveDateTime) &&
          rightNow < new Date(endDateTime);
        const isLater = rightNow < new Date(startLiveDateTime);
        const isCatchUp =
          rightNow > new Date(endDateTime) &&
          rightNow < new Date(streamExpireDateTime);

        streams.push({
          url: value.pathName,
          title: value.title,
          description: value.description,
          streamName: value.streamName,
          start_time_text: value.timeFrom,
          end_time_text: value.timeTo,
          live_date_time: startLiveDateTime,
          end_date_time: endDateTime,
          time_shifted: value.timeShifted,
          stream_expire_date_time: streamExpireDateTime,
          stream_type: value.header,
          image: value.image.path,
          wowzApplication: value.wowzaApplication,
          streamingUrl: value.hlsVideoUrl,
          mediaLive: value.mediaLive,
          time_text: `${value.timeFrom} ${EXTRA_FEED_SEPARATOR} ${value.timeTo}`,
          hide_link_text: formatTimeMin(
            lang,
            startLiveDateTime,
            strings,
            value.timeFrom
          ),
          hide_link: isLater,
          catch_up: isCatchUp,
          isLive: isLive,
          isLater: isLater,
          isCatchUp: isCatchUp
        });
      }
    });

  // Sort streams: live first, then later, then catch-up
  streams.sort((a, b) => {
    if (a.isLive && !b.isLive) return -1;
    if (!a.isLive && b.isLive) return 1;
    if (a.isLater && !b.isLater) return -1;
    if (!a.isLater && b.isLater) return 1;
    if (a.isCatchUp && !b.isCatchUp) return -1;
    if (!a.isCatchUp && b.isCatchUp) return 1;
    return 0;
  });

  return { type: EXTRAFEED_HOME_FETCH_DATA_SUCCESS, payload: streams };
}

export function fetchExtraStreamsDay(date) {
  const dateURL = catchUpTodaysDateFormat(date);
  return globalFetch({
    urlPath: 'online-exclusive-cards',
    extraParams: `&date=${dateURL}`,
    urlSrc: FEED_URLSRC_CMS_V2,
    reducerIsLoading: EXTRAFEED_DAY_IS_LOADING,
    reducerError: EXTRAFEED_DAY_HAS_ERRORED,
    dispatchSuccess: (itemObj, dispatch) => {
      dispatch(fetchExtraStreamsDaySuccess(itemObj));
    },
  });
}

export function fetchExtraStreamsDaySuccess(items) {
  const lang =
    localStorage.getItem(localStorageLang) === null
      ? 0
      : parseInt(localStorage.getItem(localStorageLang), 10);

  let streams = [];
  const rightNow = new Date();

  items.results &&
    items.results.length > 0 &&
    items.results.forEach((value) => {
      const startLiveDateTime = new Date(value.startLiveDateTime);
      const streamExpireDateTimeCropped = value.timeShifted
        ? adjustEndDateTime(
            value.startLiveDateTime,
            value.streamExpireDateTime,
            OE_CATCHUP_WINDOW_HOURS
          )
        : null;
      const streamExpireDateTime = new Date(streamExpireDateTimeCropped);
      const endDateTimeCropped = adjustEndDateTime(
        value.startLiveDateTime,
        value.endDateTime,
        OE_LIVE_WINDOW_HOURS
      );
      const endDateTime = new Date(endDateTimeCropped);

      const isLive =
        rightNow > startLiveDateTime && rightNow < endDateTime ? true : false;
      const isLater = rightNow < startLiveDateTime ? true : false;
      const isExpired =
        rightNow > streamExpireDateTime && value.timeShifted ? true : false;
      const isCatchUp =
        rightNow > endDateTime &&
        rightNow < streamExpireDateTime &&
        value.timeShifted
          ? true
          : false;
      const isUnavailable =
        rightNow > endDateTime && !value.timeShifted ? true : false;

      streams.push({
        url: value.pathName,
        title: value.title,
        description: value.description,
        streamName: value.streamName,
        start_time_text: value.timeFrom,
        end_time_text: value.timeTo,
        live_date_time: value.startLiveDateTime,
        end_date_time: value.endDateTime,
        stream_type: value.header,
        image: value.image.path,
        wowzApplication: value.wowzaApplication,
        streamingUrl: value.hlsVideoUrl,
        mediaLive: value.mediaLive,
        time_text: `${value.timeFrom} ${EXTRA_FEED_SEPARATOR} ${value.timeTo}`,
        hide_link_text: formatTimeMin(
          lang,
          value.startLiveDateTime,
          strings,
          value.timeFrom
        ),
        isLive: isLive,
        isLater: isLater,
        isExpired: isExpired,
        isCatchUp: isCatchUp,
        isUnavailable: isUnavailable
      });
    });

  return { type: EXTRAFEED_DAY_FETCH_DATA_SUCCESS, payload: streams };
}

export function fetchExtraFeed(slug) {
  return globalFetch({
    urlPath: `online-exclusive-cards`,
    extraParams: `&lang=cy&page=1&pageSize=20&days=0`,
    extraParamsLang: false,
    urlSrc: FEED_URLSRC_CMS_V2,
    reducerIsLoading: EXTRAFEED_IS_LOADING,
    reducerError: EXTRAFEED_HAS_ERRORED,
    dispatchSuccess: (ItemObj, dispatch) => {
      dispatch(fetchExtraFeedEn(ItemObj.results, slug));
    },
  });
}

export function fetchExtraFeedEn(items, slug) {
  return globalFetch({
    urlPath: `online-exclusive-cards`,
    extraParams: `&lang=en&page=1&pageSize=20&days=0`,
    extraParamsLang: false,
    urlSrc: FEED_URLSRC_CMS_V2,
    reducerIsLoading: EXTRAFEED_IS_LOADING,
    reducerError: EXTRAFEED_HAS_ERRORED,
    dispatchSuccess: (ItemObj, dispatch) => {
      dispatch(fetchExtraFeedSuccess([items, ItemObj.results], slug));
    },
  });
}

function otherLangData(id, items) {
  return items.find((item) => item.id === id);
}

export function fetchExtraFeedSuccess(items, slug) {
  let chosenFeed = {},
    otherLang = '';
  if (items && items.length > 0) {
    for (var i = 0; i < items.length; i++) {
      if (items[i] && items[i].length > 0) {
        for (var j = 0; j < items[i].length; j++) {
          otherLang = '';
          if (slug === items[i][j].pathName) {
            const startLiveDateTime = items[i][j].startLiveDateTime;
            const endDateTime = adjustEndDateTime(
              startLiveDateTime,
              items[i][j].endDateTime,
              OE_LIVE_WINDOW_HOURS
            );

            const streamExpireDateTime = items[i][j].timeShifted
              ? adjustEndDateTime(
                  startLiveDateTime,
                  items[i][j].streamExpireDateTime,
                  OE_CATCHUP_WINDOW_HOURS
                )
              : null;

            otherLang = otherLangData(items[i][j].id, items[i === 0 ? 1 : 0]);
            chosenFeed = {
              description: items[i][j].description,
              url: items[i][j].pathName,
              title: items[i][j].title,
              subTitle: items[i][j].header,
              other_url: otherLang.pathName,
              url_lang: i,
              image: items[i][j].image.path,
              stream_type: items[i][j].header,
              wowzApplication: items[i][j].wowzaApplication,
              streamingUrl: items[i][j].hlsVideoUrl + `?lang=${checkLang(i)}`,
              otherCommsUrl: items[i][j].otherCommentaryBtnText,
              streamName: items[i][j].streamName + `?lang=${checkLang(i)}`,
              startLiveDateTime: items[i][j].startLiveDateTime,
              endDateTime: endDateTime,
              timeShifted: items[i][j].timeShifted,
              streamExpireDateTime: streamExpireDateTime,
              filename: items[i][j].streamName
            };
            break;
          }
        }
      }
    }
  }

  return {
    type: EXTRAFEED_FETCH_DATA_SUCCESS,
    payload: chosenFeed,
  };
}
